import { useState, type ReactNode } from 'react'
import { Wrench, ArrowRight, Check, Clock } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../lib/shadcn/tooltip'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { useProposeCorrection, useReconcileShift } from '../../hooks/backend/shifts'
import { useCapabilities } from '../../hooks/useCapabilities'
import {
  type ReconcileProposal,
  evidenceTone,
  evidenceLabel,
  reconcileState,
  signalLabel,
  signalMeaning,
} from './reconcile'

function problemLabel(problem: string): string {
  if (problem === 'vehicle_mismatch') return 'Wrong vehicle claimed on this shift.'
  if (problem === 'duplicate_slot') return 'Duplicate slot: one car, one window, contested by two drivers.'
  return problem.replace(/_/g, ' ')
}

// The change sentence is driven by target_field, never hardcoded to vehicle,
// since some proposals flip the window instead of the car.
function changeVerb(p: ReconcileProposal): string {
  return p.target_field === 'window' ? 'Change shift window from' : 'Change vehicle from'
}

// correction_type is about WHERE the money moves, never about the driver.
// Vehicle changes are material because they move money between two owners'
// statements; window flips are minor because nothing financial moves.
function severityText(p: ReconcileProposal): string {
  if (p.changes_owner_earnings === true) {
    return "This moves the shift's revenue and costs from one vehicle owner's statement to another. The driver's pay is unaffected, but two owners' payouts change. Owner statements for the affected period will need regenerating."
  }
  return 'Same car and same owner. No money moves, this only corrects which window the shift sits in.'
}

// A disabled reconcile button with an optional tooltip and colour tint. Used
// for the applied / requested / blocked terminal states.
function DisabledReconcile({
  label,
  icon,
  tone,
  tooltip,
}: {
  label: string
  icon: ReactNode
  tone: 'green' | 'amber' | 'muted'
  tooltip?: string
}) {
  const toneClass =
    tone === 'green'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
      : tone === 'amber'
        ? 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300'
        : ''

  const button = (
    <span className="inline-flex">
      <Button
        variant="outline"
        size="sm"
        disabled
        className={`h-7 gap-1.5 px-2 text-xs disabled:opacity-100 ${toneClass}`}
      >
        {icon}
        {label}
      </Button>
    </span>
  )

  if (!tooltip) return button
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function ReconcileDialog({
  proposal,
  onProposed,
}: {
  proposal: ReconcileProposal
  // Called after a proposal is successfully filed or applied, so the caller
  // can refresh every queue the resolved shift might still be sitting in.
  onProposed: () => void
}) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { trigger: propose, loading: proposing } = useProposeCorrection()
  const { trigger: reconcileNow, loading: reconciling } = useReconcileShift()
  const { isAdmin } = useCapabilities()

  const state = reconcileState(proposal)
  const loading = proposing || reconciling
  // Admins reconcile in one click (propose + apply, atomically); everyone else
  // files a proposal for a second person to approve.
  const actionLabel = isAdmin ? 'Reconcile now' : 'Propose for approval'

  async function handleAction() {
    setError(null)
    try {
      if (isAdmin) {
        await reconcileNow({ shift_id: proposal.shift_id, notes: null }, { skipCache: true }).result
        toast.success('Shift reconciled.')
      } else {
        await propose({ shift_id: proposal.shift_id }, { skipCache: true }).result
        toast.success('Proposal filed for approval.')
      }
      setOpen(false)
      onProposed()
    } catch (err) {
      // Surface the DB function's refusal message verbatim.
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  if (state.kind === 'applied') {
    return <DisabledReconcile label="Applied" tone="green" icon={<Check className="h-3.5 w-3.5" />} />
  }
  if (state.kind === 'requested') {
    return (
      <DisabledReconcile
        label="Requested"
        tone="amber"
        icon={<Clock className="h-3.5 w-3.5" />}
        tooltip="A correction for this shift is already awaiting approval"
      />
    )
  }
  if (state.kind === 'blocked') {
    return (
      <DisabledReconcile
        label="Reconcile"
        tone="muted"
        icon={<Wrench className="h-3.5 w-3.5" />}
        tooltip={state.reason}
      />
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setError(null)
        setOpen(next)
      }}
    >
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5 px-2 text-xs"
        onClick={() => {
          setError(null)
          setOpen(true)
        }}
      >
        <Wrench className="h-3.5 w-3.5" />
        {actionLabel}
      </Button>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Reconcile shift</DialogTitle>
          <DialogDescription>{problemLabel(proposal.problem)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Proposed change, worded from target_field */}
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">{changeVerb(proposal)}</span>
              <span className="font-mono font-semibold text-foreground">
                {proposal.current_value || 'n/a'}
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-mono font-semibold text-foreground">
                {proposal.proposed_value || 'n/a'}
              </span>
            </div>
          </div>

          {/* Rationale, verbatim */}
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Rationale</p>
            <p className="text-sm leading-relaxed text-foreground">
              {proposal.rationale || 'n/a'}
            </p>
          </div>

          {/* Evidence grade plus the strongest single signal behind it */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Evidence</span>
            <Chip tone={evidenceTone(proposal.evidence_grade)}>
              {evidenceLabel(proposal.evidence_grade)}
            </Chip>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-default">
                    <Chip
                      tone={proposal.strongest_signal === 'driver_submitted_form' ? 'red' : 'neutral'}
                    >
                      {signalLabel(proposal.strongest_signal)}
                    </Chip>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {signalMeaning(proposal.strongest_signal)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-xs text-muted-foreground">
              ({proposal.correction_type} correction)
            </span>
          </div>

          {/* Severity: where the money moves, never about driver pay */}
          <div
            className={
              proposal.changes_owner_earnings === true
                ? 'flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100'
                : 'flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground'
            }
          >
            <span className="font-medium">{severityText(proposal)}</span>
          </div>

          {isAdmin && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              Reconciling as an admin proposes and applies this correction in one step. It takes
              effect immediately.
            </p>
          )}

          {/* Non-admins can only propose; make the two-step nature explicit. */}
          {!isAdmin && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              This files a proposal only. An admin must review and approve it before the shift
              is changed.
            </p>
          )}

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleAction} disabled={loading}>
            {loading && <Spinner className="mr-1.5 h-4 w-4" />}
            {actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
